import React, { useCallback, useEffect, useState } from 'react';
import * as Progress from '@radix-ui/react-progress';
import styles from '../Program.module.scss';

interface Props {
  start: number | undefined;
  end: number | undefined;
  current: () => number;
}

const ProgressBar = ({
  start = 0,
  end = 100,
  current,
  ...restProps
}: Props) => {
  const getCurrent = useCallback(() => current(), [current]);

  const getProgress = useCallback(() => {
    const now = getCurrent();
    if (start === end) return 0;

    const elapsed = Math.max(0, now - start);
    const duration = end - start;

    const result = Math.round((elapsed / duration) * 100);

    if (result > 100) return 100;
    if (result < 0) return 0;

    return result;
  }, [getCurrent, end, start]);

  const [currentProgress, setCurrentProgress] = useState<number | null>(
    getProgress()
  );

  useEffect(() => {
    setCurrentProgress(getProgress());

    const interval = setInterval(
      () => setCurrentProgress(getProgress()),
      10000
    );
    return () => clearInterval(interval);
  }, [getProgress]);

  return currentProgress ? (
    <Progress.Root
      className={styles.progressRoot}
      value={currentProgress}
      title={`${currentProgress}%`}
      {...restProps}
    >
      <Progress.Indicator
        className={styles.progressIndicator}
        style={{
          transform: `translateX(-${100 - (currentProgress || 100)}%)`,
        }}
      />
    </Progress.Root>
  ) : null;
};

export default ProgressBar;
